import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

// ---- Reveal: line-by-line masked reveal
export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.span
    className={`reveal-mask ${className}`}
    style={{ display: "block", paddingBottom: "0.08em" }}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0 }}
    variants={{ hidden: {}, show: {} }}
  >
    <motion.span
      style={{ display: "block" }}
      variants={{ hidden: { y: "110%" }, show: { y: "0%" } }}
      transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.span>
  </motion.span>
);

export const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
  >
    {children}
  </motion.div>
);

// ---- Buttons
const base = "inline-flex items-center justify-center gap-2 font-bold text-sm md:text-base transition-all duration-300 group";
export const Button = ({ children, variant = "primary", to, href, onClick, className = "", testId, type }) => {
  const styles = {
    primary: "wanm-gradient-bg text-white rounded-full px-7 py-3.5 hover:scale-[1.04] hover:shadow-[0_16px_48px_rgba(252,12,201,0.35)]",
    dark: "bg-wanm-dark text-white rounded-full px-7 py-3.5 hover:bg-black hover:scale-[1.04]",
    outline: "border-2 border-wanm-dark text-wanm-dark rounded-full px-7 py-3.5 hover:bg-wanm-dark hover:text-white",
    outlineLight: "border-2 border-white/70 text-white rounded-full px-7 py-3.5 hover:bg-white hover:text-wanm-dark",
    ghost: "text-wanm-dark rounded-full px-4 py-2 hover:text-wanm-red",
  };
  const cls = `${base} ${styles[variant]} ${className}`;
  const inner = <>{children}<ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>;
  if (to) return <Link to={to} className={cls} data-testid={testId}>{inner}</Link>;
  if (href) return <a href={href} className={cls} data-testid={testId}>{inner}</a>;
  return <button type={type || "button"} onClick={onClick} className={cls} data-testid={testId}>{inner}</button>;
};

// ---- Section header
export const SectionHeader = ({ over, title, sub, light = false, align = "left" }) => (
  <div className={`max-w-4xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    <FadeUp>
      <span className="text-sm font-black uppercase tracking-[0.25em] wanm-gradient-text">{over}</span>
    </FadeUp>
    <h2 className={`mt-5 text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] ${light ? "text-white" : "text-wanm-dark"}`}>
      <Reveal>{title}</Reveal>
    </h2>
    {sub && (
      <FadeUp delay={0.1}>
        <p className={`mt-6 text-lg md:text-xl leading-relaxed max-w-2xl ${align === "center" ? "mx-auto" : ""} ${light ? "text-white/70" : "text-wanm-dark/60"}`}>{sub}</p>
      </FadeUp>
    )}
  </div>
);

// ---- Marquee
export const Marquee = ({ words = "PERFORMANCE MARKETING", slow = false, dark = false }) => {
  const items = Array.from({ length: 6 });
  return (
    <div className={`relative w-full overflow-hidden py-8 border-y ${dark ? "border-white/10" : "border-wanm-dark/10"}`}>
      <div className={`flex whitespace-nowrap ${slow ? "marquee-slow" : "animate-marquee"}`}>
        {items.concat(items).map((_, i) => (
          <span key={i} className="flex items-center">
            <span className={`text-5xl md:text-7xl font-black tracking-tight px-6 ${i % 2 === 0 ? (dark ? "text-white" : "text-wanm-dark") : (dark ? "text-stroke-white" : "text-stroke")}`}>{words}</span>
            <span className="text-3xl wanm-gradient-text px-2">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
};
